import React from 'react';
import { Calendar } from 'lucide-react';
import { getCurrencySymbol } from '../utils/currency';
import type { UserDividendForecastEvent } from '../utils/userDividendForecast';

interface DividendEventsProps {
    events: UserDividendForecastEvent[];
    currencySymbol: string;
    horizonDays: number;
    onHorizonChange: (days: number) => void;
}

const formatDividendAmount = (value: number) =>
    value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: value >= 1 ? 2 : 4,
    });

const formatQuantity = (value: number) =>
    value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
    });

const DividendEvents: React.FC<DividendEventsProps> = ({
    events,
    currencySymbol,
    horizonDays,
    onHorizonChange,
}) => {
    const today = new Date();
    const recentCutoff = new Date(today);
    recentCutoff.setDate(recentCutoff.getDate() - 5);
    const horizon = new Date(today);
    horizon.setDate(horizon.getDate() + horizonDays);

    const filteredEvents = events.filter((event) => {
        const targetDate = new Date(`${event.eventDate}T00:00:00`);
        return !Number.isNaN(targetDate.getTime()) && targetDate >= recentCutoff && targetDate <= horizon;
    });

    const horizonOptions = [
        { label: '3M', days: 90 },
        { label: '6M', days: 180 },
        { label: '12M', days: 365 },
    ];

    return (
        <div className="bg-[#1e1e26] p-6 rounded-2xl border border-white/5 shadow-lg">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="font-bold text-slate-200">Recent & Upcoming Events</h3>
                    <p className="mt-1 text-xs text-slate-500">Confirmed and estimated events for your holdings.</p>
                </div>
                <div className="flex items-center bg-[#13131a] border border-white/10 rounded-lg p-1">
                    {horizonOptions.map((option) => (
                        <button
                            key={option.days}
                            onClick={() => onHorizonChange(option.days)}
                            className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${
                                horizonDays === option.days
                                    ? 'bg-slate-700 text-white'
                                    : 'text-slate-400 hover:text-white'
                            }`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex space-x-4 overflow-x-auto pb-2">
                {filteredEvents.length === 0 && (
                    <div className="rounded-xl border border-white/5 bg-[#13131a] px-5 py-6 text-sm text-slate-400">
                        <p className="font-semibold text-slate-200">No upcoming dividend events yet.</p>
                        <p className="mt-2">Confirmed and estimated events will appear here when available for your holdings.</p>
                    </div>
                )}
                {filteredEvents.map((event) => {
                    const displayDate = new Date(`${event.eventDate}T00:00:00`);
                    const formattedDate = displayDate.toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                    });
                    const dateLabel = event.dateType === 'PAYMENT_DATE' ? 'Payment date' : 'Ex-dividend date';
                    const nativeSymbol = event.nativeCurrency ? getCurrencySymbol(event.nativeCurrency) : '';
                    const currencyLabel = event.nativeCurrency ? null : 'Currency TBA';

                    return (
                        <div
                            key={event.id}
                            className="min-w-[260px] bg-[#13131a] border border-white/5 rounded-xl p-4 shadow-sm"
                        >
                            <div className="flex items-center space-x-2 text-slate-400 text-xs mb-3">
                                <Calendar size={14} />
                                <span>{dateLabel}: {formattedDate}</span>
                            </div>
                            <div className="font-semibold text-white flex items-center space-x-2 mb-2">
                                <span className="px-2 py-1 rounded bg-white/5 text-xs border border-white/10">{event.symbol}</span>
                                <span className={`px-2 py-1 rounded text-[10px] border ${event.declared ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'bg-amber-500/10 border-amber-500/30 text-amber-300'}`}>{event.declared ? 'Confirmed' : 'Estimated'}</span>
                                <span className="truncate max-w-[150px] text-sm text-slate-200">{event.name}</span>
                            </div>
                            <div className="text-sm text-slate-300">
                                {event.amountPerShare !== null ? (
                                    <span>{nativeSymbol}{formatDividendAmount(event.amountPerShare)}/share {currencyLabel ? `(${currencyLabel})` : ''}</span>
                                ) : (
                                    <span>Amount TBA</span>
                                )}
                            </div>
                            {event.expectedCashBase !== null && (
                                <div className="mt-2 text-sm font-semibold text-emerald-400">
                                    Expected {currencySymbol}{formatDividendAmount(event.expectedCashBase)} based on {formatQuantity(event.quantity)} shares
                                </div>
                            )}
                            {event.frequency && (
                                <div className="mt-2 text-[11px] text-slate-500">{event.frequency}</div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default DividendEvents;
